/**
 * Which single banner owns the slot above the family week's day cards.
 *
 * Three banners compete for that slot:
 *
 *   - `MissingInfoBanner` — "N students need school info before you can order."
 *     (see `listStudentsNeedingRequiredInfo`).
 *   - `AutoReloadAlert` — "your auto-order can't be funded" (server-derived;
 *     see `lib/auto-reload-alert.ts`).
 *   - `AutoOrderNudge`  — coverage promo / confirmation, which at
 *     `coverage.state === 'all'` renders a GREEN "Auto-order is on — meals are
 *     placed automatically each week." That sentence is FALSE while auto-reload
 *     is disarmed: the runner is skipping the fill and auto-pausing the
 *     subscription. Showing both stacked told the parent two opposite things.
 *
 * So the precedence is a real rule, not styling: ONE derivation
 * (`resolveFamilyWeekBannerSlot`) names the winner, and BOTH call sites ask it
 * — the alert renders only when it owns the slot, and `AutoOrderNudge` returns
 * null when it doesn't. There is no arrangement of props or CSS that can put
 * them on screen together.
 *
 * Setup wins outright because a parent whose students still need school info
 * CANNOT order at all: both the funding alert and the coverage promo are
 * premature, and the nudge's "Set up" button reads as a rival path to the
 * banner's "Finish setup". There is exactly one next step, so exactly one
 * banner. The funding alert then beats the nudge because it is the actionable,
 * money-blocking fact: coverage ("is auto-order set up?") is meaningless while
 * the auto-order that IS set up cannot be paid for.
 */

import { listStudentsNeedingRequiredInfo } from '@/api/family'
import { type AutoOrderAutoReloadAlert, useFamilySubscriptionsQuery } from '@/api/family-subscriptions'
import { useMyOrderingFamilyQuery } from '@/api/orders-family'
import { selectAutoReloadAlert } from '@/lib/auto-reload-alert'
import { eligibleAutoReloadAlerts, familyMembers } from '@/lib/family-ordering-access'

export type FamilyWeekBannerSlot = 'missing-info' | 'auto-reload-alert' | 'auto-order-nudge'

/**
 * Pure precedence. Exactly one slot owner, always — the returned value is the
 * only banner allowed to render, so "both showed" is unrepresentable.
 */
export function resolveFamilyWeekBannerSlot(
  alert: AutoOrderAutoReloadAlert | null,
  studentsNeedingInfoCount = 0,
): FamilyWeekBannerSlot {
  if (studentsNeedingInfoCount > 0) {
    return 'missing-info'
  }
  return alert ? 'auto-reload-alert' : 'auto-order-nudge'
}

/**
 * The one hook every call site consults. Reads the SAME
 * `['family-subscriptions']` and `my-ordering-family` queries on every side, so
 * the components can never be looking at different answers.
 */
export function useFamilyWeekBanner(): { slot: FamilyWeekBannerSlot; alert: AutoOrderAutoReloadAlert | null } {
  const subscriptionsQuery = useFamilySubscriptionsQuery()
  const familyQuery = useMyOrderingFamilyQuery()
  const alert = selectAutoReloadAlert(
    eligibleAutoReloadAlerts(
      subscriptionsQuery.data?.autoReloadAlerts ?? [],
      subscriptionsQuery.data?.subscriptions ?? [],
      familyMembers(familyQuery.data),
    ),
  )
  const studentsNeedingInfo = listStudentsNeedingRequiredInfo(familyQuery.data)
  return { slot: resolveFamilyWeekBannerSlot(alert, studentsNeedingInfo.length), alert }
}
